import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';

import IdsInput from '../ids-input/ids-input';
import IdsClearableMixin from '../../mixins/ids-clearable-mixin/ids-clearable-mixin';

import './ids-trigger-button';

import styles from './ids-trigger-field.scss';
import type IdsTriggerButton from './ids-trigger-button';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

const Base = IdsClearableMixin(IdsInput);

/**
 * IDS Trigger Field Component
 * @type {IdsTriggerField}
 * @inherits IdsInput
 * @mixes IdsClearableMixin
 * @part container
 * @part content-area - the content with trigger buttons and input element
 * @part field-container - element that wraps input field, trigger buttons, and inline errors (used for styling)
 * @part input - internal HTMLInputElement
 * @part popup - internal IdsPopup used for optional Autocomplete feature
 */
@customElement('ids-trigger-field')
@scss(styles)
export default class IdsTriggerField extends Base {
  /**
   * Call the constructor and then initialize
   */
  constructor() {
    super();
  }

  /**
   * Get a list of element dependencies for this component
   * @returns {object} of elements
   */
  get elements() {
    return {
      content: this.container?.querySelector('.ids-trigger-field-content'),
      label: this.container?.querySelector('label'),
      text: this.container?.querySelector('ids-text'),
    };
  }

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachTriggerButtonEvents();
    this.#setFieldHeight();
    this.readonly = stringToBool(this.getAttribute('readonly'));
    this.#attachFocusEvents();
    const datagridAttachment = getClosest(this, 'ids-data-grid');
    datagridAttachment?.cacheFilterFields(this);
  }

  /**
   * Detach all focus-related event listeners
   * @private
   */
  disconnectEvents() {
    this.#buttons = null;
    // Remove input event listeners
    if (this.input) {
      this.input.removeEventListener('focus', this.#inputFocusHandler);
      this.input.removeEventListener('blur', this.#inputBlurHandler);
      this.input.removeEventListener('keydown', this.#inputKeydownHandler);
      this.detachNativeEvents?.();
    }

    // Remove button event listeners
    this.buttons.forEach((btn) => {
      const focusHandler = this.#buttonFocusHandlers.get(btn);
      const blurHandler = this.#buttonBlurHandlers.get(btn);
      if (focusHandler) btn.removeEventListener('focus', focusHandler);
      if (blurHandler) btn.removeEventListener('blur', blurHandler);
    });

    // Clear handler maps
    this.#buttonFocusHandlers.clear();
    this.#buttonBlurHandlers.clear();
  }

  disconnectedCallback(): void {
    this.disconnectEvents();
    super.disconnectedCallback?.();
    this.#fixAlignTargetOnLocaleChange();
  }

  /**
   * Fix autocomplete popup alignTarget after locale change
   * @private
   */
  #fixAlignTargetOnLocaleChange() {
    if (!this.autocomplete) return;

    this.addEventListener('localechange', () => {
      if (this.popup && !this.popup.alignTarget) {
        this.popup.alignTarget = this.fieldContainer;
      }
    });
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.FORMAT,
      attributes.TABBABLE,
      attributes.VALIDATE
    ];
  }

  /**
   * Push color variant to the container element
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    this.buttons.forEach((btn) => { btn.colorVariant = this.colorVariant; });
  }

  /**
   * Creates the Template for rendering an IdsTriggerField's contents
   * @returns {string} containing the template
   */
  template() {
    const {
      ariaLabel,
      containerClass,
      inputClass,
      inputState,
      labelHtml,
      placeholder,
      type,
      value
    } = this.templateVariables();

    const isEventPicker = this.parentElement?.classList.contains('event-picker') ?? false;
    return `<div class="ids-trigger-field ${containerClass} ${isEventPicker ? 'event-picker' : ''}" ${this.readonly ? 'readonly' : ''} part="container">
      ${labelHtml}
      <div class="field-container" part="field-container">
        <slot name="trigger-start"></slot>
        <slot class="content-area" part="content-area"></slot>
        <input
          part="input"
          id="${this.id}-internal"
          ${type}${inputClass}${placeholder}${inputState}
          ${ariaLabel}
          ${value}
          autocomplete="off"
          ></input>
        <slot name="trigger-end"></slot>
        <slot name="loading-indicator"></slot>
      </div>
      ${this.autocomplete ? `
        <ids-popup
          type="dropdown"
          align="bottom, left"
          align-target="#${this.id}-internal"
          part="popup"
        >
          <ids-list-box slot="content" size="${this.size}"></ids-list-box>
        </ids-popup>` : ''}
    </div>`;
  }

  #buttons: IdsTriggerButton[] | null = null;

  /**
   * @readonly
   * @returns {Array<HTMLElement>} containing references to slotted IdsTriggerButtons on this component
   */
  get buttons() {
    if (!this.#buttons) {
      this.#refreshButtonCache();
    }
    return this.#buttons!;
  }

  /**
   * Refresh the button cache and reattach events
   * @private
   */
  #refreshButtonCache() {
    this.#buttons = [
      ...this.querySelectorAll<IdsTriggerButton>('ids-trigger-button'),
      ...this.shadowRoot?.querySelectorAll<IdsTriggerButton>('ids-trigger-button') ?? [],
    ];
  }

  /**
   * @param {boolean | string} value true if this trigger field's buttons should be made accessible using the tab key
   */
  set tabbable(value: boolean | string) {
    const newValue = stringToBool(value);

    if (value !== null && value !== undefined) {
      this.setAttribute(attributes.TABBABLE, stripHTML(String(newValue)));
    } else {
      this.removeAttribute(attributes.TABBABLE);
    }

    // Refresh button cache to ensure we have current buttons
    this.#buttons = null;
    this.buttons.forEach((button) => {
      button.tabbable = newValue;
    });

    // Re-attach focus events to update tab behavior
    this.#attachFocusEvents();
  }

  /**
   * @returns {boolean | string} true if this trigger field's buttons are accessible using the tab key
   */
  get tabbable(): boolean | string {
    const attr = this.getAttribute(attributes.TABBABLE);
    return attr !== null ? stringToBool(attr) : false;
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    super.disabled = value;
    // Refresh button cache to ensure we have current buttons
    this.#buttons = null;

    if (stringToBool(value)) {
      this.readonly = false;
      super.readonly = false;

      this.buttons.forEach((btn) => {
        btn.setAttribute(attributes.DISABLED, '');
        btn.readonly = false;
      });
    } else {
      this.buttons.forEach((btn) => {
        btn.removeAttribute(attributes.DISABLED);
      });
    }
  }

  get disabled(): boolean {
    return super.disabled;
  }

  /**
   * Sets the readonly attribute
   * @param {boolean} r string value from the read only attribute
   */
  set readonly(r: boolean | string) {
    super.readonly = r;
    // Refresh button cache to ensure we have current buttons
    this.#buttons = null;

    if (stringToBool(r) && !this.readonlyBackground) {
      this.buttons.forEach((btn) => {
        btn.setAttribute(attributes.READONLY, '');
        btn.removeAttribute(attributes.DISABLED);
      });
    } else {
      this.buttons.forEach((btn) => {
        btn.removeAttribute(attributes.READONLY);
      });
    }
  }

  get readonly(): boolean {
    return super.readonly;
  }

  /**
   * Sets the id internally and externally
   * @param {string} value id value
   */
  set id(value: string) {
    const shadowId = `${value}-internal`;
    this.setAttribute(attributes.ID, value);
    this.shadowRoot?.querySelector('label')?.setAttribute('for', shadowId);
    this.shadowRoot?.querySelector('input')?.setAttribute('id', shadowId);
  }

  get id(): string {
    return this.getAttribute(attributes.ID) || 'none';
  }

  /**
   * Set format for date, time
   * @param {string} val date, time format
   */
  set format(val: string | null) {
    if (val) {
      this.setAttribute(attributes.FORMAT, val);
    } else {
      this.removeAttribute(attributes.FORMAT);
    }
  }

  /**
   * Return format
   * @returns {string} return date format
   */
  get format(): string | null {
    return this.getAttribute(attributes.FORMAT);
  }

  /**
   * Establish Internal Event Handlers
   * @private
   */
  #attachTriggerButtonEvents() {
    this.onEvent('click.internal-trigger-button', this, (e: CustomEvent) => {
      if (e.detail.origin === 'input') {
        return;
      }
      const btn = (e.target as IdsTriggerButton);
      if (btn) this.trigger();
    });
  }

  #inputFocusHandler = () => {
    if (this.nodeName.toLowerCase() === 'ids-trigger-field') {
      this.fieldContainer?.classList?.add?.('is-focused');
      return;
    }

    const parent: IdsTriggerField = getClosest(this, 'ids-trigger-field');
    if (parent?.fieldContainer?.classList) {
      parent.fieldContainer.classList.add('is-focused');
    }
  };

  #inputBlurHandler = () => {
    if (this.nodeName.toLowerCase() === 'ids-trigger-field') {
      this.fieldContainer?.classList?.remove?.('is-focused');
      return;
    }

    const parent: IdsTriggerField = getClosest(this, 'ids-trigger-field');
    if (parent?.fieldContainer?.classList) {
      parent.fieldContainer.classList.add('is-focused');
    }
  };

  #inputKeydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey && this.buttons.length && this.tabbable) {
      const clearBtn = this.clearableButton;
      const tabbableButtons = this.buttons.filter((btn) => !btn.classList.contains('btn-clear'));
      if (!tabbableButtons.length) return;
      if (clearBtn && (clearBtn as any).tabbable) return;
      e.preventDefault();
      this.fieldContainer?.classList.add('hide-focus');
      tabbableButtons[0].focus();
      tabbableButtons[0].button?.classList.add('is-focused');
    }
  };

  #buttonFocusHandlers = new Map();

  #buttonBlurHandlers = new Map();

  /**
   * Attach focus related events
   * @private
   */
  #attachFocusEvents() {
    if (!this.fieldContainer || !this.input) return;

    // this.input is a native input element, it would not work with onEvent/offEvent listener
    this.input.removeEventListener('focus', this.#inputFocusHandler);
    this.input.removeEventListener('blur', this.#inputBlurHandler);
    this.input.removeEventListener('keydown', this.#inputKeydownHandler);

    this.input.addEventListener('focus', this.#inputFocusHandler);
    this.input.addEventListener('blur', this.#inputBlurHandler);
    this.input.addEventListener('keydown', this.#inputKeydownHandler);

    // Handle button focus events
    this.buttons.forEach((btn) => {
      this.offEvent('focus', btn);
      this.onEvent('focus', btn, () => {
        this.fieldContainer?.classList.add('hide-focus');
        btn.button?.classList.add('is-focused');
      });

      this.offEvent('blur', btn);
      this.onEvent('blur', btn, () => {
        this.fieldContainer?.classList.remove('hide-focus');
        btn.button?.classList.remove('is-focused');

        // If focus is returning to the input, add is-focused back
        this.fieldContainer?.classList.toggle('is-focused', document.activeElement === this.input);
      });
    });
  }

  /**
   * Set field height and compact attributes
   * @private
   * @returns {void}
   */
  #setFieldHeight(): void {
    // Refresh button cache to ensure we have current buttons
    this.#buttons = null;

    const setAttr = (btn: IdsTriggerButton, attr: string, val: string) => {
      if (this.hasAttribute(attr)) btn?.setAttribute(attr, val);
      else btn?.removeAttribute(attr);
    };

    this.buttons?.forEach((btn) => {
      setAttr(btn, attributes.COMPACT, '');
      setAttr(btn, attributes.FIELD_HEIGHT, this.fieldHeight);
    });
  }

  /**
   * Updates trigger buttons when the trigger field's fieldHeight property is updated
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string): void {
    this.buttons.forEach((btn) => {
      if (val) {
        const attr = val === 'compact' ? { name: 'compact', val: '' } : { name: 'field-height', val };
        btn.setAttribute(attr.name, attr.val);
      } else {
        btn.removeAttribute('compact');
        btn.removeAttribute('field-height');
      }
    });
  }

  /**
   * Fire the trigger event and action.
   */
  trigger(): void {
    let canTrigger = true;
    const response = (veto: any) => {
      canTrigger = !!veto;
    };
    this.triggerEvent('beforetriggerclicked', this, { detail: { elem: this, response } });

    if (!canTrigger) {
      return;
    }
    this.triggerEvent('triggerclicked', this, { detail: { elem: this } });
  }

  /**
   * Focus the trigger button (used by dropdown when tab is pressed)
   */
  focusTriggerButton(): void {
    if (this.buttons.length > 0 && this.tabbable) {
      const fieldContainer = this.container?.querySelector('.field-container');
      fieldContainer?.classList.add('hide-focus');
      this.buttons[0].focus();
      this.buttons[0].button?.classList.add('is-focused');
    }
  }

  /**
   * Override to check parent component for change event dispatch preference
   * @returns {boolean} false if parent component prevents change events
   */
  shouldDispatchChangeOnValueSet(): boolean {
    const parent = (this.getRootNode() as any).host;
    return parent?.shouldDispatchChangeOnValueSet?.() !== false;
  }

  /**
   * Target the field container for tooltip positioning to center tooltip on entire field
   * @returns {HTMLElement} The field container element for proper tooltip alignment
   */
  onTooltipTargetDetection(): HTMLElement {
    return this.fieldContainer || this.input || this;
  }
}
